import React, { useState } from "react";
import adminApi from "@/lib/adminApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

const NEW_DRINK = "__new_drink__";
const NEW_FOOD = "__new_food__";
const NEW_REST = "__new__";

const UrlImportDialog = ({ open, rows = [], onClose, onApplied }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [restaurantId, setRestaurantId] = useState("");
  const [url, setUrl] = useState("");
  const [includeDesc, setIncludeDesc] = useState(true);
  const [menus, setMenus] = useState([]);
  const [cats, setCats] = useState([]);
  const [done, setDone] = useState(null);
  const [newForm, setNewForm] = useState({ name: "", slug: "", owner_email: "" });
  const [createdCreds, setCreatedCreds] = useState(null);
  const [targetRid, setTargetRid] = useState("");

  const restaurant = rows.find((r) => r.id === restaurantId);
  const targetName = restaurantId === NEW_REST ? (newForm.name || "Nuovo ristorante") : (restaurant?.name || "");

  const analyze = async () => {
    if (!restaurantId) return toast.error("Seleziona un ristorante");
    if (!url.trim()) return toast.error("Incolla l'URL del menù");
    if (restaurantId === NEW_REST && !newForm.name.trim()) return toast.error("Inserisci almeno il nome del locale");
    setLoading(true);
    try {
      let effRid = restaurantId;
      if (restaurantId === NEW_REST) {
        const cr = await adminApi.post("/admin/restaurants/create", { name: newForm.name.trim(), slug: newForm.slug.trim() || undefined, owner_email: newForm.owner_email.trim() || undefined });
        effRid = cr.data.restaurant_id;
        setCreatedCreds({ email: cr.data.owner_email, password: cr.data.temp_password, slug: cr.data.slug });
        toast.success(`Ristorante creato: /m/${cr.data.slug}`);
      }
      setTargetRid(effRid);
      const menuRes = await adminApi.get(`/admin/restaurants/${effRid}/menus`);
      const ms = menuRes.data || [];
      setMenus(ms);
      const foodMenu = ms.find((m) => m.type === "food" && !m.is_daily) || ms.find((m) => m.type !== "bevande");
      const drinkMenu = ms.find((m) => m.type === "bevande");
      const res = await adminApi.post("/admin/import/url", { url: url.trim(), include_descriptions: includeDesc });
      const parsed = (res.data.categories || []).map((c) => {
        const isDrink = c.type === "drink";
        const menuId = isDrink ? (drinkMenu?.id || NEW_DRINK) : (foodMenu?.id || NEW_FOOD);
        return {
          name: c.name || "",
          type: c.type || "food",
          menuId,
          dishes: (c.dishes || []).map((d) => ({
            name: d.name || "",
            price: d.price ?? "",
            description: d.description || "",
            formats: Array.isArray(d.formats) ? d.formats : [],
          })),
        };
      });
      if (parsed.length === 0) return toast.error("Nessuna categoria trovata");
      setCats(parsed);
      toast.success(`Analizzate ${res.data.pages_crawled} pagine · ${parsed.length} categorie`);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'analisi dell'URL");
    } finally { setLoading(false); }
  };

  const setCatName = (i, v) => setCats((cs) => cs.map((c, idx) => idx === i ? { ...c, name: v } : c));
  const setCatMenu = (i, v) => setCats((cs) => cs.map((c, idx) => idx === i ? { ...c, menuId: v } : c));
  const setDish = (ci, di, k, v) => setCats((cs) => cs.map((c, idx) => idx !== ci ? c : { ...c, dishes: c.dishes.map((d, j) => j === di ? { ...d, [k]: v } : d) }));
  const removeDish = (ci, di) => setCats((cs) => cs.map((c, idx) => idx !== ci ? c : { ...c, dishes: c.dishes.filter((_, j) => j !== di) }));
  const removeCat = (ci) => setCats((cs) => cs.filter((_, idx) => idx !== ci));
  const addDish = (ci) => setCats((cs) => cs.map((c, idx) => idx !== ci ? c : { ...c, dishes: [...c.dishes, { name: "", price: "", description: "", formats: [] }] }));

  const apply = async () => {
    const usable = cats.filter((c) => c.name.trim() && c.dishes.some((d) => d.name.trim()));
    if (usable.length === 0) return toast.error("Niente da importare");
    setLoading(true);
    try {
      const payload = {
        restaurant_id: targetRid || restaurantId,
        categories: usable.map((c) => ({
          name: c.name,
          menu_id: c.menuId,
          dishes: c.dishes.filter((d) => d.name.trim()).map((d) => ({
            name: d.name,
            description: (d.description || "").trim(),
            price: (d.formats && d.formats.length) ? null : (d.price === "" ? null : Number(d.price)),
            formats: (d.formats || []).map((f) => ({ label: f.label || "", price: Number(f.price) || 0 })),
          })),
        })),
      };
      const res = await adminApi.post("/admin/import/apply", payload);
      const upd = res.data.dishes_updated || 0;
      setDone({ dishes: res.data.dishes_created, cats: res.data.categories_created, updated: upd, skipped: res.data.dishes_skipped || 0, updatedDetails: res.data.updated_details || [] });
      toast.success(`Importati ${res.data.dishes_created} piatti${upd ? ` · ${upd} aggiornati` : ""}${res.data.dishes_skipped ? ` · ${res.data.dishes_skipped} invariati` : ""}`);
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nell'importazione"); }
    finally { setLoading(false); }
  };

  const close = () => { setStep(1); setUrl(""); setCats([]); setMenus([]); setDone(null); setRestaurantId(""); setNewForm({ name: "", slug: "", owner_email: "" }); setCreatedCreds(null); setTargetRid(""); onClose(); };

  const credsBanner = createdCreds && (
    createdCreds.password ? (
      <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm" data-testid="new-restaurant-creds">
        <p className="font-semibold text-emerald-800">Account ristoratore creato ✓</p>
        <p className="text-emerald-900 mt-1">Comunica queste credenziali al cliente (potrà cambiare la password dal suo profilo):</p>
        <div className="mt-2 font-mono text-xs bg-white rounded-lg border border-emerald-200 p-2 space-y-0.5">
          <div>Login: <span className="select-all">{window.location.origin}/login</span></div>
          <div>Email: <span className="select-all">{createdCreds.email}</span></div>
          <div>Password temporanea: <span className="select-all font-semibold">{createdCreds.password}</span></div>
        </div>
      </div>
    ) : (
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="new-restaurant-creds">
        <p className="font-semibold text-amber-800">Ristorante creato (senza email) ✓</p>
        <p className="text-amber-900 mt-1">Nessun account di accesso è stato generato. Quando avrai l'email del cliente, aggiungila dal pannello (pulsante <b>Slug/Dominio</b>) per creare le sue credenziali.</p>
      </div>
    )
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="url-import-dialog">
        <DialogHeader><DialogTitle>Importa menù da URL (migrazione)</DialogTitle></DialogHeader>

        {step === 1 && (
          <div className="py-2 space-y-4">
            <p className="text-sm text-muted-foreground">Incolla il link della pagina di selezione del menù esistente del cliente. Il sistema segue i link alle pagine di menù collegate, estrae categorie, piatti e prezzi e li organizza per il ristorante scelto. Puoi importare più URL in momenti diversi sullo stesso ristorante: le categorie già presenti vengono riutilizzate e vengono aggiunti solo i piatti mancanti (nessun duplicato). Nulla viene pubblicato: potrai controllare tutto prima di salvare.</p>
            <div>
              <label className="text-sm font-medium">Ristorante di destinazione</label>
              <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)} data-testid="url-import-restaurant" className="mt-1 w-full rounded-xl border border-input px-3 py-2.5 bg-white focus:outline-none focus:border-[#002FA7]">
                <option value="">— Seleziona —</option>
                <option value={NEW_REST}>➕ Crea nuovo ristorante</option>
                {rows.map((r) => <option key={r.id} value={r.id}>{r.name} (/m/{r.slug})</option>)}
              </select>
            </div>
            {restaurantId === NEW_REST && (
              <div className="rounded-xl border border-[#002FA7]/30 bg-[#002FA7]/5 p-4 space-y-3" data-testid="new-restaurant-form">
                <p className="text-sm font-semibold text-[#002FA7]">Nuovo ristorante</p>
                <div>
                  <label className="text-xs font-medium">Nome del locale *</label>
                  <input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} data-testid="new-rest-name" placeholder="Es. Circus Cafe" className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]" />
                </div>
                <div>
                  <label className="text-xs font-medium">Slug / sottodominio</label>
                  <input value={newForm.slug} onChange={(e) => setNewForm({ ...newForm, slug: e.target.value })} data-testid="new-rest-slug" placeholder="circus-cafe (se vuoto, generato dal nome)" className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]" />
                </div>
                <div>
                  <label className="text-xs font-medium">Email del ristoratore (opzionale)</label>
                  <input value={newForm.owner_email} onChange={(e) => setNewForm({ ...newForm, owner_email: e.target.value })} data-testid="new-rest-email" type="email" placeholder="cliente@email.it (puoi aggiungerla dopo)" className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]" />
                </div>
                <p className="text-xs text-muted-foreground">Se inserisci l'email, verrà generata una password temporanea da comunicare al cliente. Puoi anche creare il ristorante senza email e aggiungerla in seguito.</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">URL del menù esistente</label>
              <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="url-import-url" placeholder="https://www.esempio.com/nomeristorante" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-border p-3 hover:bg-muted/40">
              <input type="checkbox" checked={includeDesc} onChange={(e) => setIncludeDesc(e.target.checked)} data-testid="url-import-desc" className="mt-0.5 w-4 h-4 accent-[#002FA7]" />
              <span className="text-sm">Importa anche le descrizioni dei piatti (in italiano).</span>
            </label>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {credsBanner}
            <p className="text-sm text-muted-foreground">Controlla e correggi prima di importare in <span className="font-semibold">{targetName}</span>. Assegna ogni categoria al menù corretto.</p>
            {cats.map((c, ci) => (
              <div key={ci} className="rounded-xl border border-border p-3" data-testid={`url-cat-${ci}`}>
                <div className="flex items-center gap-2 mb-2">
                  <input value={c.name} onChange={(e) => setCatName(ci, e.target.value)} data-testid={`url-cat-name-${ci}`} placeholder="Categoria" className="flex-1 font-semibold rounded-lg border border-input px-3 py-1.5 focus:outline-none focus:border-[#002FA7]" />
                  <button onClick={() => removeCat(ci)} className="p-1.5 text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-muted-foreground">Menù:</span>
                  <select value={c.menuId} onChange={(e) => setCatMenu(ci, e.target.value)} data-testid={`url-cat-menu-${ci}`} className="flex-1 rounded-lg border border-input px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-[#002FA7]">
                    {menus.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    <option value={NEW_FOOD}>➕ Crea menù “Menù”</option>
                    <option value={NEW_DRINK}>➕ Crea menù “Drink List”</option>
                  </select>
                </div>
                {c.dishes.map((d, di) => (
                  <div key={di} className="mb-2">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1">
                        <input value={d.name} onChange={(e) => setDish(ci, di, "name", e.target.value)} data-testid={`url-dish-name-${ci}-${di}`} placeholder="Piatto" className="w-full rounded-lg border border-input px-3 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]" />
                        {d.formats && d.formats.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1 pl-1">Varianti: {d.formats.map((f) => `${f.label} €${f.price}`).join(" · ")}</p>
                        )}
                      </div>
                      {(!d.formats || d.formats.length === 0) && (
                        <input value={d.price} onChange={(e) => setDish(ci, di, "price", e.target.value)} type="number" step="0.01" placeholder="€" className="w-20 rounded-lg border border-input px-2 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]" />
                      )}
                      <button onClick={() => removeDish(ci, di)} className="p-1.5 text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    {includeDesc && (
                      <input value={d.description || ""} onChange={(e) => setDish(ci, di, "description", e.target.value)} placeholder="Descrizione (italiano)" className="w-full mt-1 rounded-lg border border-input px-3 py-1.5 text-xs text-muted-foreground focus:outline-none focus:border-[#002FA7]" />
                    )}
                  </div>
                ))}
                <button onClick={() => addDish(ci)} className="text-xs text-[#002FA7] font-semibold flex items-center gap-1 mt-1"><Plus className="w-3 h-3" /> Piatto</button>
              </div>
            ))}
          </div>
        )}

        {step === 3 && done && (
          <div className="py-4 space-y-4" data-testid="url-import-done">
            {credsBanner}
            <div className="text-center">
              <p className="text-base font-semibold">Import completato ✓</p>
              <p className="text-sm text-muted-foreground mt-1">{done.dishes} piatti in {done.cats} nuove categorie aggiunti a {targetName}.{done.updated ? ` ${done.updated} piatti esistenti aggiornati (prezzi/descrizioni).` : ""}{done.skipped ? ` ${done.skipped} già presenti e invariati.` : ""} Tutto in bozza: le traduzioni si possono generare dal pannello del ristorante.</p>
            </div>
            {done.updatedDetails?.length > 0 && (
              <div className="text-left rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="url-updated-report">
                <p className="text-sm font-semibold text-amber-800 mb-2">Piatti aggiornati ({done.updatedDetails.length})</p>
                <ul className="space-y-1.5">
                  {done.updatedDetails.map((u, i) => (
                    <li key={i} className="text-xs text-amber-900 flex flex-wrap items-center gap-x-2" data-testid={`url-updated-row-${i}`}>
                      <span className="font-medium">{u.name}</span>
                      <span className="text-amber-700">· {u.category}</span>
                      {u.price_changed && (u.old_price !== u.new_price) && (
                        <span className="font-mono">{u.old_price} → <span className="font-semibold">{u.new_price}</span></span>
                      )}
                      {u.desc_changed && <span className="italic">descrizione aggiornata</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step !== 3 && <button onClick={close} className="rounded-full px-5 py-2 text-sm font-medium hover:bg-muted">Annulla</button>}
          {step === 1 && <button onClick={analyze} disabled={loading} data-testid="url-analyze-btn" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a] disabled:opacity-60 flex items-center gap-2">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />} Analizza URL</button>}
          {step === 2 && <button onClick={apply} disabled={loading} data-testid="url-apply-btn" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a] disabled:opacity-60 flex items-center gap-2">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Importa</button>}
          {step === 3 && <button onClick={() => { onApplied(); close(); }} data-testid="url-done-close" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a]">Chiudi</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UrlImportDialog;
